'use client';
import React from 'react';
import {
  FacebookIcon,
  type IconT,
  LinkedInIcon,
  TwitterIcon,
} from '@mentimeter/ragnar-visuals';
import { usePathname, useSearchParams } from '@mentimeter/next-navigation';
import { getFullURL } from 'src/utils/getFullURL';
import { getPathnameWithoutLocale } from '@mentimeter/i18n';
import { LOCALES } from 'src/constants.cjs';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Clickable } from '../actions';

const TWITTER_SHARE_MESSAGE = encodeURIComponent(
  'I just read this interesting Mentimeter blog article',
);
const TWITTER_HASHTAGS = 'mentimeter';

const createUtmTags = (media: string, slug: string) =>
  `?utm_campaign=blog&utm_medium=share&utm_source=${media}&utm_content=${slug}`;

interface Item {
  id: string;
  icon: React.ComponentType<IconT>;
  createHref: (href: string, slug: string, title: string) => string;
  windowName: string;
  windowFeatures: string;
}

const items: Item[] = [
  {
    id: 'facebook',
    icon: FacebookIcon,
    createHref: (href, slug, _) =>
      `https://www.facebook.com/v2.9/dialog/share?app_id=224322077663587&display=popup&href=${href}${createUtmTags(
        'facebook',
        slug,
      )}`,
    windowName: 'facebook-share-dialog',
    windowFeatures: 'width=626,height=436',
  },
  {
    id: 'linked-in',
    icon: LinkedInIcon,
    createHref: (href, _, title) =>
      `https://www.linkedin.com/shareArticle?mini=true&url=${href}&title=${title}`,
    windowName: 'linkedin-share-dialog',
    windowFeatures: 'width=626,height=496',
  },
  {
    id: 'twitter',
    icon: TwitterIcon,
    createHref: (href, slug, _) =>
      `http://www.twitter.com/intent/tweet?url=${href}${createUtmTags(
        'twitter',
        slug,
      )}&via=mentimeter&text=${TWITTER_SHARE_MESSAGE}&hashtags=${TWITTER_HASHTAGS}`,
    windowName: 'twitter-share-dialog',
    windowFeatures: 'width=606,height=306',
  },
];

const handleClick = (
  e: React.MouseEvent,
  {
    fullHref,
    windowName,
    windowFeatures,
  }: Pick<Item, 'windowName' | 'windowFeatures'> & { fullHref: string },
) => {
  e.preventDefault();
  window.open(fullHref, windowName, windowFeatures);
};

interface ShareT {
  slug: string;
  title: string;
}

const ORIGIN = process.env.NEXT_PUBLIC_ORIGIN;

export const Share = ({ slug, title }: ShareT) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const pathnameWithoutLocale = getPathnameWithoutLocale(pathname, LOCALES);
  const href = getFullURL(
    ORIGIN + pathnameWithoutLocale,
    searchParams?.toString(),
  );

  return (
    <Box flexDirection="row" width={1} justifyContent="center">
      {items.map(({ id, icon: Icon, createHref, ...item }, index) => (
        <Clickable
          key={id}
          mx={index === 1 ? 'space4' : 'space0'}
          onClick={(e) =>
            handleClick(e, {
              fullHref: createHref(href, slug, title),
              ...item,
            })
          }
          bg="primary"
          href={createHref(href, slug, title)}
          borderRadius={3}
          p="space4"
          aria-label={`Share post on ${id}`}
          extend={({ theme }) => ({
            transition: `all ${theme.durations[1]}s ease`,
            ':hover': {
              transform: 'scale(1.1)',
              opacity: 1,
            },
            ':active': {
              transform: 'scale(0.95)',
              opacity: 1,
            },
          })}
        >
          <Icon size={3} color="onPrimary" />
        </Clickable>
      ))}
    </Box>
  );
};
